import { makeAutoObservable } from "mobx";

import { TeamBillingStore } from "@/store/team/TeamBillingStore";
import { UpdateTeamForm } from "@/store/team/UpdateTeamForm";
import { UpdateUserForm } from "@/store/user/UpdateUserForm";

import type { TeamModel } from "@/models/TeamModel";
import type { RootStore } from "@/store/RootStore";

export const TEAM_DIALOG_TABS = ["account", "settings", "members", "billing"] as const;

export type TeamDialogTab = (typeof TEAM_DIALOG_TABS)[number];

export class TeamDialogStore {
  rootStore: RootStore;

  isOpen = false;

  team: TeamModel | null = null;

  updateTeamForm: UpdateTeamForm;

  updateUserForm: UpdateUserForm;

  billing: TeamBillingStore | null = null;

  isBillingEnabled = false;

  private requestedTab: TeamDialogTab | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.updateTeamForm = new UpdateTeamForm(rootStore);
    this.updateUserForm = new UpdateUserForm(rootStore);
    makeAutoObservable(this);
  }

  get availableTabs(): TeamDialogTab[] {
    const { team } = this;
    const isBillingAvailable = this.isBillingEnabled && Boolean(team?.userPermissions.billing);
    const billingTabs: TeamDialogTab[] = isBillingAvailable ? ["billing"] : [];

    if (!team || team.personal) return ["account", ...billingTabs];
    if (this.rootStore.classroom.isStudentIn(team)) return [];
    return ["settings", "members", ...billingTabs];
  }

  get activeTab(): TeamDialogTab | null {
    const { availableTabs, requestedTab } = this;
    if (requestedTab && availableTabs.includes(requestedTab)) return requestedTab;
    return availableTabs[0] ?? null;
  }

  setActiveTab(tab: TeamDialogTab) {
    this.requestedTab = tab;
  }

  setBillingEnabled(isEnabled: boolean) {
    this.isBillingEnabled = isEnabled;
  }

  open(team?: TeamModel, tab?: TeamDialogTab) {
    this.updateUserForm = new UpdateUserForm(this.rootStore);

    const selectedTeam = team ?? this.rootStore.teams.currentTeam;
    if (selectedTeam) {
      this.setTeam(selectedTeam);
    }

    this.requestedTab = tab ?? null;
    this.isOpen = true;
  }

  selectTeam(team: TeamModel) {
    if (this.team?.uid === team.uid) return;

    this.setTeam(team);
  }

  close() {
    this.isOpen = false;
    this.billing?.dispose();
  }

  private setTeam(team: TeamModel) {
    this.billing?.dispose();
    this.team = team;
    this.updateTeamForm = new UpdateTeamForm(this.rootStore, team);
    this.billing = new TeamBillingStore(team);
  }
}
